import { Schedule } from "@iventis/domain-model/model/schedule";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { ScheduleConfigVersionType } from "@iventis/domain-model/model/scheduleConfigVersionType";
import { v4 as uuid } from "uuid";
import { createDefaultEventBlockTypes, createEventBlockCodeDataField, createEventBlockStyleDataField } from "./edit-event-block-type-helpers";
import { createDefaultScheduleVersionTypes, createDiscplineDataField } from "./edit-schedule-version-type.helpers";
import { createVenueCodeDatafield } from "./edit-venue-types-helpers";

export enum ConfigSectionsKey {
    timesAndDates,
    venueTypeHierarchy,
    scheduleVersionHierarchy,
    eventBlockHierarchy,
    venueRunSheets,
}

/* eslint-disable @typescript-eslint/no-namespace */
declare namespace Intl {
    function DateTimeFormat(): {
        resolvedOptions(): {
            timeZone: string;
        };
    };
}

export const createEmptySchedule = (): Schedule => {
    const venueCodeDataFieldVenue = createVenueCodeDatafield();

    const spaceCodeDataField = createVenueCodeDatafield();

    const defaultEventBlockCodeDataField = createEventBlockCodeDataField();

    const defaultEventBlockStyleDataField = createEventBlockStyleDataField();

    const topLevelVenueId = uuid();

    const secondLevelVenueId = uuid();

    const defaultEventBlockTypes = createDefaultEventBlockTypes(defaultEventBlockCodeDataField.id, defaultEventBlockStyleDataField.id, topLevelVenueId, secondLevelVenueId);

    const disciplineDataField = createDiscplineDataField();

    const defaultScheduleVersionTypes = createDefaultScheduleVersionTypes(disciplineDataField.id);

    const defaultVenueLocationTypes: ScheduleConfigVenueLocationType[] = [
        {
            id: topLevelVenueId,
            name: "Venue",
            allowedParentType: undefined,
            dataFields: [venueCodeDataFieldVenue.id],
            apiNamePlural: undefined,
            apiNameSingular: undefined,
            customerIdDataFieldId: venueCodeDataFieldVenue.id,
        },
        {
            id: secondLevelVenueId,
            name: "Space",
            allowedParentType: topLevelVenueId,
            dataFields: [spaceCodeDataField.id],
            apiNamePlural: undefined,
            apiNameSingular: undefined,
            customerIdDataFieldId: spaceCodeDataField.id,
        },
    ];

    return {
        projectId: undefined /* Set in the route */,
        config: {
            name: undefined /* Route matches the top-level name */,
            enableRecurringEvents: true,
            dataFields: [venueCodeDataFieldVenue, spaceCodeDataField, disciplineDataField, defaultEventBlockCodeDataField, defaultEventBlockStyleDataField],
            dcasConfig: {
                dataFields: [],
            },
            scheduleVersionTypes: defaultScheduleVersionTypes,
            eventBlockTypes: defaultEventBlockTypes,
            venueLocationTypes: defaultVenueLocationTypes,
        },
        name: "Schedule",
        startAt: undefined,
        endAt: undefined,
        dayStartAt: undefined,
        dayEndAt: undefined,
        dayZero: undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    } as Schedule;
};

export const timesAndDatesValidation = (schedule: Schedule) =>
    schedule?.dayStartAt !== "" &&
    schedule?.dayStartAt != null &&
    schedule?.dayEndAt !== "" &&
    schedule?.dayEndAt != null &&
    schedule?.startAt != null &&
    schedule?.endAt != null &&
    schedule?.dayZero != null;

export const venueTypeHierarchyValidation = (venueLocationTypes: ScheduleConfigVenueLocationType[]) =>
    venueLocationTypes != null ? venueLocationTypes.every(({ name }) => name !== "" && name != null) : false;

export const scheduleVersionTypeValidation = (scheduleVersionTypes: ScheduleConfigVersionType[]) =>
    scheduleVersionTypes != null ? scheduleVersionTypes.every(({ name }) => name !== "" && name != null) : false;

export const eventBlockTypeValidation = (eventBlockTypes: ScheduleConfigEventBlockType[]) =>
    eventBlockTypes != null ? eventBlockTypes.filter(({ calendarLevel }) => calendarLevel != null).every(({ name }) => name !== "" && name != null) : false;

export const runSheetValidation = (eventBlockTypes: ScheduleConfigEventBlockType[]) =>
    eventBlockTypes != null ? eventBlockTypes.filter(({ calendarLevel }) => calendarLevel == null).every(({ name }) => name !== "" && name != null) : false;
